import React from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { C, SHADOW } from '../../merchants/shared/tokens'
import { SR_ONLY } from '../components/SetupUi'

/**
 * Sticky pill stepper under the top bar, after D2C WizardStepper, but driven by the route and the merchant's data
 * instead of a Redux stage: a step is done when its data is there, locked when a step before it must be done first
 * (the reason shows on hover), and any other step can be opened.
 *
 * @param states  { [stepKey]: { done, lockedBy } } from MerchantStatus.stepStates
 */
export default function PtlStepper({ steps, current, states, onGo }) {
  return (
    <Box sx={{
      position: 'sticky', top: 56, zIndex: 35, bgcolor: C.surface, borderBottom: `1px solid ${C.line}`,
      borderRadius: '0 0 12px 12px', boxShadow: SHADOW.card,
    }}>
      <Box component='ol' aria-label='Onboarding steps' sx={{
        listStyle: 'none', m: 0, px: 2, py: 0.75, maxWidth: 1640, mx: 'auto',
        display: 'flex', alignItems: 'center', overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' },
      }}>
        {steps.map((s, i) => {
          const st = states[s.key] || {}
          const active = s.key === current
          const locked = !active && !st.done && !!st.lockedBy
          const done = !active && st.done
          return (
            <React.Fragment key={s.key}>
              {i > 0 && (
                <Box component='li' aria-hidden sx={{
                  flex: 1, minWidth: 12, height: 1.5, borderRadius: 99, mx: 0.5,
                  bgcolor: done || active || states[steps[i - 1].key]?.done ? C.accent : C.line,
                }} />
              )}
              <Box component='li' sx={{ flexShrink: 0 }}>
                <Tooltip title={locked ? st.lockedBy : done ? `${s.label}: done` : ''} arrow placement='bottom'>
                  <span>
                    <Box component='button' type='button' disabled={locked} aria-current={active ? 'step' : undefined}
                      onClick={() => !active && !locked && onGo(s.key)}
                      sx={{
                        all: 'unset', boxSizing: 'border-box', position: 'relative', display: 'flex', alignItems: 'center', gap: 0.75,
                        minHeight: 32, px: 1.25, py: 0.6, borderRadius: '20px', userSelect: 'none',
                        cursor: active || locked ? 'default' : 'pointer', transition: 'background-color 180ms, box-shadow 180ms',
                        '&:focus-visible': { outline: `2px solid ${C.accent}`, outlineOffset: 2 },
                        ...(active && { bgcolor: C.accent, boxShadow: '0 2px 10px rgba(67,56,202,0.30)' }),
                        ...(done && { bgcolor: C.okSoft, '&:hover': { bgcolor: C.okBorder } }),
                        ...(!active && !done && !locked && { border: `1px dashed ${C.lineStrong}`, '&:hover': { borderColor: C.accent, bgcolor: C.accentSoft } }),
                        ...(locked && { opacity: 0.6 }),
                      }}>
                      <Box component='span' aria-hidden sx={{
                        width: 20, height: 20, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0,
                        fontSize: 10.5, fontWeight: 800, lineHeight: 1,
                        ...(active && { bgcolor: 'rgba(255,255,255,0.25)', color: '#fff' }),
                        ...(done && { bgcolor: C.ok, color: '#fff' }),
                        ...(!active && !done && { bgcolor: C.surfaceAlt, color: C.inkMuted }),
                      }}>
                        {done ? <CheckIcon sx={{ fontSize: 13 }} /> : locked ? <LockOutlinedIcon sx={{ fontSize: 12 }} /> : i + 1}
                      </Box>
                      <Typography component='span' noWrap sx={{
                        fontSize: 12, lineHeight: 1, fontWeight: active ? 700 : 600,
                        color: active ? '#fff' : done ? C.ok : C.inkMuted,
                      }}>
                        Step {i + 1} · {s.label}
                        <Box component='span' sx={SR_ONLY}>
                          {active ? ' (current)' : done ? ' (done)' : locked ? ' (locked)' : ''}
                        </Box>
                      </Typography>
                    </Box>
                  </span>
                </Tooltip>
              </Box>
            </React.Fragment>
          )
        })}
      </Box>
    </Box>
  )
}
